import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input } from 'reactstrap';
import Select from 'react-select';
import { useIntl } from 'react-intl';
import { APIsearchGoogleFonts } from '../../../../api/Pages';
import {
  DEFAULT_GOOGLE_WEIGHTS,
  normalizeFontPickerValue,
} from './pageFontUtils';

const FontPickerField = ({
  value,
  onChange,
  onOpenFilePicker,
  leadingControl,
  trailingControl,
}) => {
  const intl = useIntl();
  const normalized = useMemo(() => normalizeFontPickerValue(value), [value]);
  const [googleResults, setGoogleResults] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);

  const isGoogleSource = normalized.source === 'google';
  const isUploadedSource = normalized.source === 'uploaded';
  const hasUploadedPath = !!normalized.path;

  const googleSelectStyles = useMemo(() => ({
    menuPortal: base => ({ ...base, zIndex: 99999999 }),
    menu: base => ({ ...base, zIndex: 99999999 }),
  }), []);

  useEffect(() => {
    if (normalized.source !== 'google') {
      return () => {};
    }

    let cancelled = false;
    setGoogleLoading(true);

    APIsearchGoogleFonts(normalized.googleSearch || '', 12)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setGoogleResults(data);
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGoogleLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [normalized.googleSearch, normalized.source]);

  const selectedGoogleFont = googleResults.find(
    result => result.family === normalized.googleFamily,
  );
  const availableWeights = useMemo(() => {
    if (selectedGoogleFont?.weights?.length) {
      return selectedGoogleFont.weights;
    }

    if (normalized.googleWeights?.length) {
      return normalized.googleWeights;
    }

    return DEFAULT_GOOGLE_WEIGHTS;
  }, [normalized.googleWeights, selectedGoogleFont]);

  const googleFamilyOption = normalized.googleFamily
    ? { value: normalized.googleFamily, label: normalized.googleFamily }
    : null;

  const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;

  const getGoogleWeight = (weights, fallbackWeight) => {
    if (weights?.[0]) {
      return weights[0];
    }

    if (fallbackWeight) {
      return fallbackWeight;
    }

    return '400';
  };

  const emit = (patch) => {
    const base = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    onChange({
      ...base,
      ...normalized,
      ...patch,
    });
  };

  const handleSourceChange = (nextSource) => {
    const isGoogle = nextSource === 'google';
    const normalizedGoogleWeights = normalized.googleWeights?.length
      ? normalized.googleWeights
      : DEFAULT_GOOGLE_WEIGHTS;

    emit({
      source: nextSource,
      uuid: isGoogle ? '' : normalized.uuid,
      path: isGoogle ? '' : normalized.path,
      url: '',
      fileName: isGoogle ? '' : normalized.fileName,
      uploadedViaLogoUploader: isGoogle ? false : !!normalized.uploadedViaLogoUploader,
      googleFamily: isGoogle ? (normalized.googleFamily || 'Inter') : '',
      googleWeight: isGoogle ? (normalized.googleWeight || '400') : '400',
      googleWeights: isGoogle ? normalizedGoogleWeights : DEFAULT_GOOGLE_WEIGHTS,
      googleFiles: isGoogle ? (normalized.googleFiles || {}) : {},
      googleSearch: isGoogle ? normalized.googleSearch : '',
    });
  };

  const openPicker = () => {
    onOpenFilePicker((fileData) => {
      if (!fileData) return;

      const selectedFileName = fileData.display_name
        || fileData.original_name
        || fileData.path?.split('/').pop()
        || '';

      const selectedFileUrl = fileData.urls?.original
        || fileData.urls?.preview
        || '';

      emit({
        source: 'uploaded',
        uuid: fileData.uuid || '',
        path: fileData.path || '',
        fileName: selectedFileName,
        url: selectedFileUrl,
        uploadedViaLogoUploader: !!fileData.uploaded_via_logo_uploader,
      });
    });
  };

  return (
    <>
      <div className="puck-fonts-field-controls-row">
        {leadingControl}
        <Input
          type="select"
          bsSize="sm"
          value={normalized.source}
          onChange={e => handleSourceChange(e.target.value)}
          className="puck-fonts-field-select-source"
        >
          <option value="uploaded">
            {intl.formatMessage({ defaultMessage: 'Uploaded font file' })}
          </option>
          <option value="google">
            {intl.formatMessage({ defaultMessage: 'Google Font' })}
          </option>
        </Input>
        {trailingControl}
      </div>

      {isUploadedSource && (
        <div className="puck-fonts-field-controls-row">
          <Button size="sm" color="primary" onClick={openPicker} className="puck-fonts-field-btn-no-shrink">
            {hasUploadedPath
              ? intl.formatMessage({ defaultMessage: 'Change file' })
              : intl.formatMessage({ defaultMessage: 'Select font file' })}
          </Button>
          {hasUploadedPath && (
            <span className="puck-fonts-field-file-name">
              {normalized.fileName || normalized.path.split('/').pop()}
            </span>
          )}
          {hasUploadedPath && (
            <Button
              size="sm"
              color="link"
              style={{ padding: 0 }}
              onClick={() => emit({
                uuid: '',
                path: '',
                fileName: '',
                uploadedViaLogoUploader: false,
              })}
            >
              <i className="far fa-trash mr-0" />
            </Button>
          )}
        </div>
      )}

      {isGoogleSource && (
        <>
          <Select
            classNamePrefix="puck-fonts-google-family-select"
            className="mb-1"
            isClearable
            isSearchable
            placeholder={intl.formatMessage({ defaultMessage: 'Type to search Google Fonts' })}
            value={googleFamilyOption}
            isLoading={googleLoading}
            onInputChange={(inputValue, meta) => {
              if (meta.action !== 'input-change') return inputValue;
              emit({ googleSearch: inputValue });
              return inputValue;
            }}
            onChange={(option) => {
              const selected = googleResults.find(result => result.family === option?.value);
              const googleWeights = selected?.weights || DEFAULT_GOOGLE_WEIGHTS;
              emit({
                googleFamily: option?.value || '',
                googleWeights,
                googleWeight: getGoogleWeight(googleWeights, normalized.googleWeight),
                googleFiles: selected?.files || {},
              });
            }}
            options={googleResults.map(result => ({ value: result.family, label: result.family }))}
            menuPlacement="auto"
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            closeMenuOnScroll={false}
            menuShouldBlockScroll={true}
            styles={googleSelectStyles}
          />
          <Input
            className="mb-1"
            type="select"
            bsSize="sm"
            value={normalized.googleWeight || '400'}
            onChange={e => emit({ googleWeight: e.target.value })}
          >
            {availableWeights.map(weight => (
              <option key={weight} value={weight}>
                {weight}
              </option>
            ))}
          </Input>
        </>
      )}
    </>
  );
};

FontPickerField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      source: PropTypes.oneOf(['uploaded', 'google']),
      uuid: PropTypes.string,
      path: PropTypes.string,
      fileName: PropTypes.string,
      uploadedViaLogoUploader: PropTypes.bool,
      googleFamily: PropTypes.string,
      googleWeight: PropTypes.string,
      googleWeights: PropTypes.array,
      googleFiles: PropTypes.object,
      googleSearch: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  onOpenFilePicker: PropTypes.func.isRequired,
  leadingControl: PropTypes.node,
  trailingControl: PropTypes.node,
};

FontPickerField.defaultProps = {
  value: '',
  leadingControl: null,
  trailingControl: null,
};

export default FontPickerField;
